import type { ClientOfferDTO, ClientOfferLineItem } from './clientOffer.types';
import { normalizeMaterialMode, type MaterialMode } from './materialMode';
import { roundMoney, sumMoney } from './money';

export interface ClientOfferItemRow {
  label: string;
  quantity: string;
  quantityValue: number;
  unit: string;
  unitPrice: number;
  total: number;
}

export function buildClientOfferItemRows(data: Pick<ClientOfferDTO, 'items' | 'workNet'> & Partial<ClientOfferDTO>): ClientOfferItemRow[] {
  const rows: ClientOfferItemRow[] = [];

  for (const item of data.items) {
    const normalizedName = normalizeText(item.name);
    const isPlaster = isPlasterItem(normalizedName);
    const isSanding = isSandingItem(normalizedName);

    if (isSanding) {
      const plasterRow = rows.find((row) => row.label === 'Wykonanie gładzi z obróbką i szlifowaniem');
      if (plasterRow) {
        mergeOfferRows(plasterRow, item);
        continue;
      }
    }

    rows.push({
      label: isPlaster ? 'Wykonanie gładzi z obróbką i szlifowaniem' : cleanClientItemLabel(item.name),
      quantity: formatQuantity(item.quantity),
      quantityValue: item.quantity,
      unit: formatClientUnit(item.unit),
      unitPrice: item.unitPrice,
      total: item.total,
    });
  }

  if (rows.length > 0) {
    return rows;
  }

  return [{
    label: 'Zakres prac do potwierdzenia',
    quantity: '1',
    quantityValue: 1,
    unit: 'kpl.',
    unitPrice: data.workNet,
    total: data.workNet,
  }];
}

function mergeOfferRows(row: ClientOfferItemRow, item: ClientOfferLineItem): void {
  row.total = sumMoney([row.total, item.total]);
  if (row.quantityValue > 0) {
    row.unitPrice = roundMoney(row.total / row.quantityValue);
  }
}

function cleanClientItemLabel(label: string): string {
  return label
    .replace(/\bgladz\b/gi, 'gładź')
    .replace(/\bszpachlowanie\b/gi, 'szpachlowanie')
    .trim();
}

function isPlasterItem(normalizedLabel: string): boolean {
  return normalizedLabel.includes('gladz') || normalizedLabel.includes('szpachl') || normalizedLabel.includes('gips');
}

function isSandingItem(normalizedLabel: string): boolean {
  return normalizedLabel.includes('szlif') && normalizedLabel.includes('gladz');
}

function formatQuantity(value: number): string {
  return value.toLocaleString('pl-PL', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
    maximumFractionDigits: 2,
  });
}

function formatClientUnit(unit: string): string {
  if (normalizeUnit(unit) === 'm2') return 'm²';
  if (normalizeText(unit).includes('szt')) return 'szt.';
  return unit || 'kpl.';
}

export function getClientMaterialModeLabel(mode: MaterialMode): string {
  const normalized = normalizeMaterialMode(mode);
  if (normalized === 'CLIENT_MATERIALS') return 'Materiały po stronie klienta';
  if (normalized === 'INFO_MATERIALS') return 'Materiały informacyjnie';
  if (normalized === 'MIXED_MATERIALS') return 'Materiały mieszane';
  return 'Materiały po stronie wykonawcy';
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

function normalizeUnit(unit: string): string {
  return normalizeText(unit).replace(/[²2]/g, '2');
}
